/**
 * Voice Agent report HTTP client — separate NestJS backend from the revamp API.
 *
 * Base path: `${REACT_APP_API_ENDPOINT_VOICE_AGENT}/api/voice-agent` (no `/api/v1` prefix —
 * this is a distinct controller group, see the Voice Agent Report API Contract doc).
 * All endpoints are GET, return plain (unencrypted) JSON, and use standard HTTP
 * status codes with `{ statusCode, message }` error bodies.
 *
 * Sends the same bearer token as the other Reports API clients (see
 * `reportsApi.ts`'s `getReportToken`) — read from `CREDENTIALS` in
 * localStorage and attached as `Authorization: bearer <token>`.
 *
 * NOTE: `Authorization` is not a CORS-safelisted header, so this now forces
 * a preflight OPTIONS request on every call (previously avoided on purpose —
 * see git history). The Voice Agent backend's OPTIONS handler must answer
 * with valid CORS headers or every call here will fail silently.
 */
import { CREDENTIALS } from '../constants';
 /* status codes with `{ statusCode, message }` error bodies. Unlike the revamp API,
 * these endpoints are unauthenticated — no Authorization header is sent.
 *
 * Deliberately sends only the `Accept` header (no `Content-Type` — these are
 * bodyless GETs, so it's not describing anything real anyway). `Content-Type`
 * isn't a CORS-safelisted header value, so setting it forces a preflight
 * OPTIONS request — and this backend's OPTIONS handler 404s with no CORS
 * headers, silently blocking every call. Without it, the request qualifies
 * as a CORS-simple GET (no preflight), which the backend's real GET handlers
 * already answer with a correct `Access-Control-Allow-Origin`.
 */
import { messageFromRevampErrorBody } from '../utils/revampApiErrorMessage';

const VOICE_AGENT_BASE = (process.env.REACT_APP_API_ENDPOINT_VOICE_AGENT || '').replace(/\/$/, '');

/** `/api/voice-agent` — VoiceAgentController */
export const VOICE_AGENT_API_BASE = `${VOICE_AGENT_BASE}/api/voice-agent`;

// Same resolution as reportsApi.ts's getReportToken / chatbotReportsApi.ts's getChatbotToken.
function getVoiceAgentToken(): string | null {
  try {
    const raw = localStorage.getItem(CREDENTIALS);
    if (!raw) return null;
    const stored = JSON.parse(raw);
    const cred = stored?.credentials ?? stored;
    const t = cred?.accessToken ?? cred?.access_token ?? cred?.token ?? cred?.idToken ?? cred?.id_token ?? cred?.jwtToken ?? cred?.jwt;
    return t && typeof t === 'string' && t.trim() ? t.trim() : null;
  } catch {
    return null;
  }
}

function getJson(url: string, signal?: AbortSignal): Promise<any> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  const authToken = getVoiceAgentToken();
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }
  return fetch(url, { headers, signal }).then(async (res) => {
    let data: any = {};
    try {
      data = await res.json();
    } catch {
      /* ignore */
    }
    if (!res.ok) {
      throw new Error(messageFromRevampErrorBody(data, `Voice Agent API request failed (${res.status})`));
    }
    return data;
  });
}

function buildQuery(params: Record<string, string | number | undefined | null>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

export interface VARange {
  locationId: string;
  from?: string;
  to?: string;
}

/* Shared by call-volume-by-day and orders-by-day: the server buckets `days[]`
 * by range length rather than always returning one entry per calendar day.
 * ≤14 business days → "day" (unchanged, one entry per day, no endDate).
 * ≤84 days → "week", ≤365 days → "month", beyond that → "year" — each entry
 * then spans `date`…`endDate` and its numeric fields are the SUM across
 * every business day in that bucket, not a per-day average. */
export type DayBucketGranularity = 'day' | 'week' | 'month' | 'year';

/* ─── 1. Voice Agent Summary ────────────────────────────────────────────── */

export interface VoiceAgentSummary {
  totalCalls: number;
  totalCallsDeltaPercent: number | null;
  totalUniqueCalls: number;
  totalUniqueCallsDeltaPercent: number | null;
  avgCallsPerDay: number;
  busiestDay: { date: string; count: number } | null;

  ordersPlaced: number;
  ordersPlacedDeltaPercent: number | null;
  avgOrderValue: number;
  avgOrderValueDeltaPercent: number | null;
  avgItemsPerOrder: number;

  conversionRate: number;
  conversionRateDeltaPp: number | null;

  totalRevenue: number;
  totalRevenueDeltaPercent: number | null;
  topItemByRevenue: string | null;

  totalTransfers: number;
  transferRate: number;
  transferRateDeltaPp: number | null;
}

export function getVoiceAgentSummary({ locationId, from, to }: VARange, signal?: AbortSignal): Promise<VoiceAgentSummary> {
  const qs = buildQuery({ location_id: locationId, from, to });
  return getJson(`${VOICE_AGENT_API_BASE}/summary${qs}`, signal);
}

/* ─── 2. Orders By Day ──────────────────────────────────────────────────── */

export interface OrdersByDayItem {
  date: string;
  endDate?: string;
  count: number;
}

export interface OrdersByDayResponse {
  granularity: DayBucketGranularity;
  days: OrdersByDayItem[];
}

export function getVoiceAgentOrdersByDay({ locationId, from, to }: VARange, signal?: AbortSignal): Promise<OrdersByDayResponse> {
  const qs = buildQuery({ location_id: locationId, from, to });
  return getJson(`${VOICE_AGENT_API_BASE}/orders-by-day${qs}`, signal);
}

/* ─── 3. Recent Voice Orders ────────────────────────────────────────────── */

export interface VoiceOrderItemPreview {
  name: string;
  quantity: number;
  totalPrice: number;
}

export interface VoiceOrderRow {
  callSid: string;
  orderNo: string | null;
  customerName: string | null;
  time: string;
  itemsCount: number;
  itemsPreview: VoiceOrderItemPreview[] | null;
  amount: number;
  status: string | null;
}

export interface PaginationBlock {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface VoiceOrdersResponse {
  data: VoiceOrderRow[];
  pagination: PaginationBlock;
}

export function getVoiceAgentOrders(
  { locationId, from, to }: VARange,
  page = 1,
  limit = 25,
  signal?: AbortSignal,
): Promise<VoiceOrdersResponse> {
  const qs = buildQuery({ location_id: locationId, from, to, page, limit });
  return getJson(`${VOICE_AGENT_API_BASE}/orders${qs}`, signal);
}

/* ─── 4. Call Transcript ────────────────────────────────────────────────── */

export interface CallTranscriptLine {
  role: 'assistant' | 'user';
  content: string;
}

export interface CallTranscriptOrderModifier {
  modifierOptionName: string;
  optionPrice: number;
  quantity: number;
}

export interface CallTranscriptOrderItem {
  itemName: string;
  quantity: number;
  price: number;
  modifiers: CallTranscriptOrderModifier[];
}

export interface CallTranscriptOrder {
  orderNo: string;
  items: CallTranscriptOrderItem[];
  total: number;
  tax: number;
  tip: number;
  gratuity: number;
  discount: number;
  deliveryCharges: number;
  deliveryFeeDiscount: number;
  convenienceFee: number;
  cardProcessingFee: number;
  grandTotal: number;
}

export interface CallTranscriptResponse {
  callSid: string;
  customerName: string | null;
  startedAt: string;
  duration: string;
  outcome: string | null;
  transcript: CallTranscriptLine[];
  order: CallTranscriptOrder | null;
}

export function getVoiceAgentCallTranscript(callSid: string, signal?: AbortSignal): Promise<CallTranscriptResponse> {
  return getJson(`${VOICE_AGENT_API_BASE}/calls/${encodeURIComponent(callSid)}/transcript`, signal);
}

/* ─── 5. Conversion Funnel ──────────────────────────────────────────────── */

export interface ConversionFunnelResponse {
  totalCalls: number;
  orderingIntent: number;
  orderingIntentDropCount: number;
  orderingIntentDropPercent: number;
  ordersStarted: number;
  ordersStartedDropCount: number;
  ordersStartedDropPercent: number;
  ordersConfirmed: number;
  ordersConfirmedDropCount: number;
  ordersConfirmedDropPercent: number;
  ordersSubmitted: number;
  ordersSubmittedDropCount: number;
  ordersSubmittedDropPercent: number;
  totalTransfers: number;
  transferRate: number;
  overallConversionRate: number;
  intentToOrderRate: number;
}

export function getVoiceAgentConversionFunnel({ locationId, from, to }: VARange, signal?: AbortSignal): Promise<ConversionFunnelResponse> {
  const qs = buildQuery({ location_id: locationId, from, to });
  return getJson(`${VOICE_AGENT_API_BASE}/conversion-funnel${qs}`, signal);
}

/* ─── 6. Call Activity Log ──────────────────────────────────────────────── */

export interface CallActivityLogRow {
  callSid: string;
  time: string;
  customerName: string | null;
  customerNumber: string | null;
  orderNo: string | null;
  duration: string;
  outcome: string | null;
  revenue: number | null;
}

export interface CallActivityLogResponse {
  data: CallActivityLogRow[];
  pagination: PaginationBlock;
}

export function getVoiceAgentCallActivityLog(
  { locationId, from, to }: VARange,
  opts: { outcome?: string; sortBy?: 'duration' | 'time' | 'revenue'; sortOrder?: 'asc' | 'desc'; page?: number; limit?: number } = {},
  signal?: AbortSignal,
): Promise<CallActivityLogResponse> {
  const { outcome, sortBy, sortOrder, page = 1, limit = 25 } = opts;
  const qs = buildQuery({ location_id: locationId, from, to, outcome, sortBy, sortOrder, page, limit });
  return getJson(`${VOICE_AGENT_API_BASE}/call-activity-log${qs}`, signal);
}

/* ─── 7. Call Activity Log — Outcome Filter Options ─────────────────────── */

export interface CallActivityLogOutcomesResponse {
  outcomes: string[];
}

export function getVoiceAgentCallActivityLogOutcomes(locationId: string, signal?: AbortSignal): Promise<CallActivityLogOutcomesResponse> {
  const qs = buildQuery({ location_id: locationId });
  return getJson(`${VOICE_AGENT_API_BASE}/call-activity-log/outcomes${qs}`, signal);
}

/* ─── 8. Top Items Ordered via Voice ────────────────────────────────────── */

export interface TopItemRow {
  rank: number;
  itemName: string;
  orderCount: number;
  revenue: number;
  percentOfVoiceOrders: number;
}

export interface TopItemsResponse {
  data: TopItemRow[];
}

export function getVoiceAgentTopItems({ locationId, from, to }: VARange, signal?: AbortSignal): Promise<TopItemsResponse> {
  const qs = buildQuery({ location_id: locationId, from, to });
  return getJson(`${VOICE_AGENT_API_BASE}/top-items${qs}`, signal);
}

/* ─── 9. Time Saved (ROI Card) ──────────────────────────────────────────── */

export interface TimeSavedResponse {
  hours: number;
  minutes: number;
  laborCostEstimate: number;
  hourlyRate: number;
  callsHandledWithoutTransfer: number;
  avgCallDurationMinutes: number;
}

export function getVoiceAgentTimeSaved({ locationId, from, to }: VARange, signal?: AbortSignal): Promise<TimeSavedResponse> {
  const qs = buildQuery({ location_id: locationId, from, to });
  return getJson(`${VOICE_AGENT_API_BASE}/time-saved${qs}`, signal);
}

/* ─── 10. Call Volume by Hour ────────────────────────────────────────────── */

export interface CallVolumeByHourItem {
  hour: number;
  calls: number;
}

export interface CallVolumeByHourResponse {
  hours: CallVolumeByHourItem[];
}

// `from`/`to` are optional — omitting both now defaults to a single business
// day (today's business date in the outlet's timezone), not a 7-day window.
// `calls` (avg per hour) is divided by business-days, not calendar days.
export function getVoiceAgentCallVolumeByHour({ locationId, from, to }: VARange, signal?: AbortSignal): Promise<CallVolumeByHourResponse> {
  const qs = buildQuery({ location_id: locationId, from, to });
  return getJson(`${VOICE_AGENT_API_BASE}/call-volume-by-hour${qs}`, signal);
}

/* ─── 11. Call Volume by Day ─────────────────────────────────────────────── */

export interface CallVolumeByDayItem {
  date: string;
  endDate?: string;
  totalCalls: number;
  ordersSubmitted: number;
  revenue: number;
  qualityScore: number | null;
}

export interface CallVolumeByDayResponse {
  granularity: DayBucketGranularity;
  days: CallVolumeByDayItem[];
}

// `from`/`to` are optional — omitting both now defaults to a single business
// day (today's business date in the outlet's timezone), not a 7-day window.
// Each entry's `date` is a business-date label (5AM–5AM local boundary), not
// a UTC calendar day. When `granularity` is above "day" (the range spans
// more than ~14 business days), each entry is a bucket: `date`…`endDate`
// span multiple business days and `totalCalls`/`ordersSubmitted`/`revenue`
// are the SUM across that whole bucket, not a per-day figure.
export function getVoiceAgentCallVolumeByDay({ locationId, from, to }: VARange, signal?: AbortSignal): Promise<CallVolumeByDayResponse> {
  const qs = buildQuery({ location_id: locationId, from, to });
  return getJson(`${VOICE_AGENT_API_BASE}/call-volume-by-day${qs}`, signal);
}

/* ─── 12. Outcome Distribution ───────────────────────────────────────────── */

export interface OutcomeDistributionItem {
  outcome: string;
  count: number;
}

export interface OutcomeDistributionResponse {
  outcomes: OutcomeDistributionItem[];
}

export function getVoiceAgentOutcomeDistribution({ locationId, from, to }: VARange, signal?: AbortSignal): Promise<OutcomeDistributionResponse> {
  const qs = buildQuery({ location_id: locationId, from, to });
  return getJson(`${VOICE_AGENT_API_BASE}/outcome-distribution${qs}`, signal);
}

/* ─── 13. Voice Revenue KPI ──────────────────────────────────────────────── */

export interface RevenueKpiResponse {
  totalRevenue: number;
  avgOrderValue: number;
  topItemByRevenue: string | null;
  topItemRevenue: number;
}

// `from`/`to` are optional here — callers omit both for the "Today" date
// filter per the Voice Agent Report API contract (buildQuery drops
// undefined values, so the query string simply has no from/to in that case).
export function getVoiceAgentRevenueKpi({ locationId, from, to }: VARange, signal?: AbortSignal): Promise<RevenueKpiResponse> {
  const qs = buildQuery({ location_id: locationId, from, to });
  return getJson(`${VOICE_AGENT_API_BASE}/revenue-kpi${qs}`, signal);
}

/* ─── 14. Top Revenue Items ──────────────────────────────────────────────── */

export interface TopRevenueItemRow {
  name: string;
  orders: number;
  revenue: number;
  percentageOfRevenue: number;
}

export interface TopRevenueItemsResponse {
  items: TopRevenueItemRow[];
}

// `from`/`to` are optional — omit both for the "Today" date filter
// (buildQuery drops undefined values, and the backend now defaults an
// omitted range to a single business day rather than a 7-day window).
export function getVoiceAgentTopRevenueItems({ locationId, from, to }: VARange, signal?: AbortSignal): Promise<TopRevenueItemsResponse> {
  const qs = buildQuery({ location_id: locationId, from, to });
  return getJson(`${VOICE_AGENT_API_BASE}/top-revenue-items${qs}`, signal);
}

/* ─── 15. Transfers ───────────────────────────────────────────────────────── */

export interface TransferRow {
  callId: string;
  startTime: string;
  durationSeconds: number;
  transferReason: string | null;
  callerExcerpt: string;
}

export interface TransfersResponse {
  totalTransfers: number;
  transferRate: number;
  transfers: TransferRow[];
}

export function getVoiceAgentTransfers({ locationId, from, to }: VARange, signal?: AbortSignal): Promise<TransfersResponse> {
  const qs = buildQuery({ location_id: locationId, from, to });
  return getJson(`${VOICE_AGENT_API_BASE}/transfers${qs}`, signal);
}

/* ─── 16. Transfer Reasons ────────────────────────────────────────────────── */

export interface TransferReasonRow {
  reasonLabel: string;
  count: number;
  sharePct: number;
}

export interface TransferReasonsResponse {
  totalCalls: number;
  totalTransfers: number;
  transferRate: number;
  previousTotalTransfers: number;
  previousTransferRate: number;
  reasons: TransferReasonRow[];
}

// Powers the Total Transfers / Transfer Rate mini-KPIs and the Transfer
// Reasons breakdown — pre-aggregated server-side, unlike /transfers' raw
// call list. `previousTotalTransfers`/`previousTransferRate`'s comparison
// window is the preceding business-date period, not calendar days.
export function getVoiceAgentTransferReasons({ locationId, from, to }: VARange, signal?: AbortSignal): Promise<TransferReasonsResponse> {
  const qs = buildQuery({ location_id: locationId, from, to });
  return getJson(`${VOICE_AGENT_API_BASE}/transfer-reasons${qs}`, signal);
}
